import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import {
  customerStatusColorMap,
  customerStatusDisplay,
  CustomerStatusDisplay as CustomerStatusDisplayType,
  TicketStatus,
} from "../../common/constants";

// 顧客受注不可時のお問い合わせフォームURL
export const CONTACT_FORM_URL = "https://cadtrace.jp/form/";

// 内部ステータス番号から顧客向け3軸表示定義を取得する。
export function getCustomerStatusDisplay(
  status: number
): CustomerStatusDisplayType | undefined {
  return customerStatusDisplay[TicketStatus[status]];
}

// 受注不可（COMPANY_ORDER_REJECTED = 4）かどうか。
export function isOrderRejected(status: number): boolean {
  return TicketStatus[status] === "COMPANY_ORDER_REJECTED";
}

// 状態バッジ（色付き Chip）。
export const CustomerStatusBadge: React.FC<{
  status: number;
  size?: "small" | "medium";
}> = ({ status, size = "small" }) => {
  const display = getCustomerStatusDisplay(status);
  if (!display) return null;
  return (
    <Chip
      label={display.label}
      size={size}
      sx={{
        bgcolor: customerStatusColorMap[display.color].hex,
        color: "#fff",
        fontWeight: "bold",
      }}
    />
  );
};

// 担当バッジ（弊社／お客様）。owner が空の場合は表示しない。
export const CustomerOwnerBadge: React.FC<{
  status: number;
  size?: "small" | "medium";
}> = ({ status, size = "small" }) => {
  const display = getCustomerStatusDisplay(status);
  if (!display || !display.owner) return null;
  return (
    <Chip
      label={`担当: ${display.owner}`}
      size={size}
      variant="outlined"
      sx={{
        borderColor: display.owner === "弊社" ? "#1976D2" : "#F9A825",
        color: display.owner === "弊社" ? "#1976D2" : "#B26A00",
      }}
    />
  );
};

// 次アクション文（狭幅時ツールチップで全文表示）。
export const CustomerNextAction: React.FC<{
  status: number;
  noWrap?: boolean;
}> = ({ status, noWrap = true }) => {
  const display = getCustomerStatusDisplay(status);
  if (!display) return null;
  return (
    <Tooltip title={display.nextAction}>
      <Typography
        variant="body2"
        noWrap={noWrap}
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {display.nextAction}
      </Typography>
    </Tooltip>
  );
};

// 受注不可時の「お問い合わせフォームを開く」ボタン。
export const ContactFormButton: React.FC<{ size?: "small" | "medium" }> = ({
  size = "small",
}) => (
  <Button
    href={CONTACT_FORM_URL}
    target="_blank"
    rel="noopener noreferrer"
    variant="contained"
    color="error"
    size={size}
  >
    お問い合わせフォームを開く
  </Button>
);

// 3軸表示（バッジ + 次アクション + 担当）を1行にまとめて表示する。
// 受注不可時はフォーム導線ボタンも表示する。
export const CustomerStatusRow: React.FC<{
  status: number;
}> = ({ status }) => {
  const display = getCustomerStatusDisplay(status);
  if (!display) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        minWidth: 0,
      }}
    >
      <CustomerStatusBadge status={status} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <CustomerNextAction status={status} />
      </Box>
      <CustomerOwnerBadge status={status} />
      {isOrderRejected(status) && <ContactFormButton />}
    </Box>
  );
};
